import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { FileText, Phone, Globe } from "lucide-react";
const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"Terms of Service | Neuro Wellness TMS Centers of America"}
        description={
          "Read the Terms of Service for Neuro Wellness TMS Centers of America. Learn the terms and conditions governing use of our website and services."
        }
        canonical={"/terms"}
        keywords={
          "terms of service, terms and conditions, Neuro Wellness terms, website terms, Coral Springs FL terms"
        }
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero Banner */}
        <section className="bg-gradient-navy text-primary-foreground py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/20 px-3.5 py-1 text-xs sm:text-sm font-semibold text-accent">
              <FileText className="h-4 w-4" /> Legal Agreement
            </span>
            <h1 className="mt-4 font-headings text-3xl font-bold sm:text-4xl md:text-5xl text-white">
              Terms of Service
            </h1>
            <p className="mt-4 text-sm sm:text-base text-primary-foreground/70">
              Last updated: May 16, 2025
            </p>
          </div>
        </section>

        {/* Content Body */}
        <section className="container-px mx-auto max-w-4xl py-12 sm:py-16">
          <div className="bg-card rounded-2xl border border-border p-6 sm:p-10 shadow-soft space-y-8">
            {/* Service */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Service
              </h2>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed">
                These Terms of Service govern your use of the website located at
                https://neurowellnesstms.com/ and any related services provided
                by Neuro Wellness TMS Centers of America.
              </p>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed">
                By accessing https://neurowellnesstms.com/, you agree to abide
                by these Terms of Service and to comply with all applicable laws
                and regulations. If you do not agree with these Terms of
                Service, you are prohibited from using or accessing this website
                or using any other services provided by Neuro Wellness TMS
                Centers of America.
              </p>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed">
                We, Neuro Wellness TMS Centers of America, reserve the right to
                review and amend any of these Terms of Service at our sole
                discretion. Upon doing so, we will update this page. Any changes
                to these Terms of Service will take effect immediately from the
                date of publication.
              </p>
              <p className="text-sm text-muted-foreground italic">
                These Terms of Service were last updated on May 16, 2025.
              </p>
            </div>

            <hr className="border-border" />

            {/* Limitations of Use */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Limitations of Use
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                By using this website, you warrant on behalf of yourself, your
                users, and other parties you represent that you will not:
              </p>
              <ul className="list-disc pl-6 space-y-1.5 text-foreground/85 text-base leading-relaxed">
                <li>
                  modify, copy, prepare derivative works of, decompile, or
                  reverse engineer any materials and software contained on this
                  website;
                </li>
                <li>
                  remove any copyright or other proprietary notations from any
                  materials and software on this website;
                </li>
                <li>
                  transfer the materials to another person or "mirror" the
                  materials on any other server;
                </li>
                <li>
                  knowingly or negligently use this website or any of its
                  associated services in a way that abuses or disrupts our
                  networks or any other service Neuro Wellness TMS Centers of
                  America provides;
                </li>
                <li>
                  use this website or its associated services to transmit or
                  publish any harassing, indecent, obscene, fraudulent, or
                  unlawful material;
                </li>
                <li>
                  use this website or its associated services in violation of
                  any applicable laws or regulations;
                </li>
                <li>
                  use this website in conjunction with sending unauthorized
                  advertising or spam;
                </li>
                <li>
                  harvest, collect, or gather user data without the user's
                  consent; or
                </li>
                <li>
                  use this website or its associated services in such a way that
                  may infringe the privacy, intellectual property rights, or
                  other rights of third parties.
                </li>
              </ul>
            </div>

            <hr className="border-border" />

            {/* Intellectual Property */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Intellectual Property
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                The intellectual property in the materials contained in this
                website are owned by or licensed to Neuro Wellness TMS Centers
                of America and are protected by applicable copyright and
                trademark law. We grant our users permission to download one
                copy of the materials for personal, non-commercial transitory
                use.
              </p>
              <p className="text-foreground/85 text-base leading-relaxed">
                This constitutes the grant of a license, not a transfer of
                title. This license shall automatically terminate if you violate
                any of these restrictions or the Terms of Service, and may be
                terminated by Neuro Wellness TMS Centers of America at any time.
              </p>
            </div>

            <hr className="border-border" />

            {/* Liability */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Liability
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                Our website and the materials on our website are provided on an
                'as is' basis. To the extent permitted by law, Neuro Wellness
                TMS Centers of America makes no warranties, expressed or
                implied, and hereby disclaims and negates all other warranties
                including, without limitation, implied warranties or conditions
                of merchantability, fitness for a particular purpose, or
                non-infringement of intellectual property, or other violation of
                rights.
              </p>
              <p className="text-foreground/85 text-base leading-relaxed">
                In no event shall Neuro Wellness TMS Centers of America or its
                suppliers be liable for any consequential loss suffered or
                incurred by you or any third party arising from the use or
                inability to use this website or the materials on this website,
                even if Neuro Wellness TMS Centers of America or an authorized
                representative has been notified, orally or in writing, of the
                possibility of such damage.
              </p>
              <p className="text-foreground/85 text-base leading-relaxed">
                In the context of this agreement, "consequential loss" includes
                any consequential loss, indirect loss, real or anticipated loss
                of profit, loss of benefit, loss of revenue, loss of business,
                loss of goodwill, loss of opportunity, loss of savings, loss of
                reputation, loss of use and/or loss or corruption of data,
                whether under statute, contract, equity, tort (including
                negligence), indemnity or otherwise.
              </p>
              <p className="text-foreground/85 text-base leading-relaxed">
                Because some jurisdictions do not allow limitations on implied
                warranties, or limitations of liability for consequential or
                incidental damages, these limitations may not apply to you.
              </p>
            </div>

            <hr className="border-border" />

            {/* Accuracy of Materials */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Accuracy of Materials
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                The materials appearing on our website are not comprehensive and
                are for general information purposes only. Neuro Wellness TMS
                Centers of America does not warrant or make any representations
                concerning the accuracy, likely results, or reliability of the
                use of the materials on this website, or otherwise relating to
                such materials or on any resources linked to this website.
              </p>
            </div>

            <hr className="border-border" />

            {/* Links */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Links
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                Neuro Wellness TMS Centers of America has not reviewed all of
                the sites linked to its website and is not responsible for the
                contents of any such linked site. The inclusion of any link does
                not imply endorsement, approval or control by Neuro Wellness TMS
                Centers of America of the site. Use of any such linked site is
                at your own risk and we strongly advise you make your own
                investigations with respect to the suitability of those sites.
              </p>
            </div>

            <hr className="border-border" />

            {/* Right to Terminate */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Right to Terminate
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                We may suspend or terminate your right to use our website and
                terminate these Terms of Service immediately upon written notice
                to you for any breach of these Terms of Service.
              </p>
            </div>

            <hr className="border-border" />

            {/* Severance */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Severance
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                Any term of these Terms of Service which is wholly or partially
                void or unenforceable is severed to the extent that it is void
                or unenforceable. The validity of the remainder of these Terms
                of Service is not affected.
              </p>
            </div>

            <hr className="border-border" />

            {/* Governing Law */}
            <div className="space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Governing Law
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                These Terms of Service are governed by and construed in
                accordance with the laws of FL. You irrevocably submit to the
                exclusive jurisdiction of the courts in that State or location.
              </p>
            </div>

            <hr className="border-border" />

            {/* Contact Us */}
            <div className="rounded-xl bg-light-blue/40 border border-light-blue p-6 sm:p-8 space-y-4">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                Contact Us
              </h2>
              <p className="text-foreground/85 text-base leading-relaxed">
                For any questions or concerns regarding these Terms of Service,
                you may contact us using the following details:
              </p>
              <div className="pt-2 grid sm:grid-cols-2 gap-4 text-sm sm:text-base">
                <div className="flex items-center gap-3 bg-white p-3.5 rounded-lg border border-border shadow-xs">
                  <Phone className="h-5 w-5 text-accent shrink-0" />
                  <div>
                    <span className="text-xs text-muted-foreground block font-medium">
                      Phone
                    </span>
                    <a
                      href="tel:9548279793"
                      className="font-bold text-primary hover:text-accent transition-colors"
                    >
                      954-827-9793
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white p-3.5 rounded-lg border border-border shadow-xs sm:col-span-2">
                  <Globe className="h-5 w-5 text-accent shrink-0" />
                  <div>
                    <span className="text-xs text-muted-foreground block font-medium">
                      Website
                    </span>
                    <a
                      href="/contact"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-primary hover:text-accent transition-colors"
                    >
                      neurowellnesstms.com/contact
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
export default Terms;
